import { normalizeYaw } from "../shared/angles.js";
import { PHYSICS_VERSION, TICK_RATE } from "../shared/types.js";
import { presentPlayer } from "./presentation.js";
import { Renderer } from "./render.js";
import { Controls } from "./input.js";
import { Sound } from "./audio.js";
import { Network } from "./net.js";
import { settings } from "./settings.js";
import { request, toast, escape } from "./api.js";
import { World } from "../shared/collision.js";
import { spawn, step } from "../shared/movement.js";
import { record, frames, formatTime } from "../shared/replay.js";
import { speed } from "../shared/vector.js";
import type {
  MapData,
  State,
  PackedInput,
  Profile,
  Replay,
  Cosmetic,
} from "../shared/types.js";
export class Game {
  renderer: Renderer;
  controls: Controls;
  audio = new Sound();
  network = new Network();
  world?: World;
  state?: State;
  map?: MapData;
  user?: Profile;
  cosmetics: Cosmetic[] = [];
  menu = true;
  debug = false;
  noclip = false;
  spectating = "";
  thirdPerson = false;
  replayMode = false;
  onEnter = () => {};
  onMenu = () => {};
  onFinish = (result: any) => {};
  onMap = () => {};
  onLobby = () => {};
  pb: number | null = null;
  private loadId = "";
  private restartGeneration = 0;
  private inputs: PackedInput[] = [];
  private ticket = "";
  private hash = "";
  private accumulator = 0;
  private last = performance.now();
  private lastRender = 0;
  private lastNet = 0;
  private lastPing = 0;
  private lastStepSound = 0;
  private submitting = false;
  private previousPosition?: [number, number, number];
  private hudAt = 0;
  private tickSampleAt = performance.now();
  private tickSample = 0;
  private actualDraw = performance.now();
  readonly performance = {
    fps: 0,
    frameMs: 0,
    simulationMs: 0,
    renderMs: 0,
    uiMs: 0,
    longFrames: 0,
    ticks: 0,
    physicsHz: 0,
    networkHz: 0,
    networkReceiveHz: 0,
  };
  private ghostFrames: State[] = [];
  private replayFrames: State[] = [];
  private replayTick = 0;
  private fps = 0;
  private loading = 0;
  private localPractice = false;
  private hud = document.querySelector<HTMLElement>("#hud")!;
  constructor(canvas: HTMLCanvasElement) {
    this.renderer = new Renderer(canvas);
    this.controls = new Controls(canvas);
    this.controls.onReady = (message) => {
      this.menu = false;
      this.onEnter();
      let overlay = document.querySelector<HTMLElement>("#capture-prompt");
      if (!overlay) {
        overlay = document.createElement("div");
        overlay.id = "capture-prompt";
        document.body.append(overlay);
      }
      overlay.textContent = message;
    };
    this.controls.onLock = () => {
      document.querySelector("#capture-prompt")?.remove();
      this.menu = false;
      this.onEnter();
    };
    this.controls.onMenu = () => {
      document.querySelector("#capture-prompt")?.remove();
      this.menu = true;
      this.onMenu();
    };
    this.controls.onRestart = () =>
      void this.restart().catch((e) => toast(e.message));
    this.controls.onInspect = () => {
      this.renderer.inspect();
      this.audio.play("inspect");
    };
    this.controls.onDraw = () => this.renderer.drawWeapon();
    this.controls.onSlash = () => {
      this.renderer.slash();
      this.audio.play("slash");
    };
    this.network.onEvent = toast;
    this.network.onFinish = (r) => toast(r.text);
    this.network.onLobby = (room) => {
      this.renderer.roster(
        room?.players ?? [],
        this.user?.id ?? "",
        this.cosmetics,
      );
      if (room && room.mapId !== this.map?.id && room.mapId !== this.loadId)
        void this.load(room.mapId).catch((e) => toast(e.message));
      this.onLobby();
    };
    requestAnimationFrame((t) => this.frame(t));
  }
  async preview() {}
  identify(user: Profile, cosmetics: Cosmetic[]) {
    this.user = user;
    this.cosmetics = cosmetics;
    this.renderer.equip(user, cosmetics);
  }
  private loadingPhase(phase: string) {
    let el = document.querySelector<HTMLElement>("#loading-screen");
    if (!el) {
      el = document.createElement("div");
      el.id = "loading-screen";
      document.body.append(el);
    }
    el.innerHTML =
      "<small>" +
      escape(this.loadId.toUpperCase()) +
      "</small><h2>LOADING MAP</h2><p>" +
      escape(phase) +
      "</p>";
  }
  async load(id: string) {
    this.loadId = id;
    this.loadingPhase("MANIFEST / COLLISION");
    try {
      const generation = ++this.loading;
      const result = await request<{ map: MapData; hash: string }>(
        "/api/maps/" + id,
      );
      if (generation !== this.loading) return;
      this.map = result.map;
      this.hash = result.hash;
      this.world = new World(this.map);
      await this.renderer.load(this.map, (phase) => this.loadingPhase(phase));
      if (generation !== this.loading) return;
      this.ghostFrames = [];
      this.renderer.setGhost(null);
      await this.restart();
      this.loadId = "";
      this.onMap();
    } finally {
      document.querySelector("#loading-screen")?.remove();
    }
  }
  async restart() {
    if (!this.map) return;
    const generation = ++this.restartGeneration;
    this.ticket = "";
    this.state = undefined;
    this.noclip = false;
    this.localPractice = this.map.ranked === false;
    this.replayMode = false;
    this.spectating = "";
    this.submitting = false;
    this.inputs = [];
    this.accumulator = 0;
    const result = await request("/api/runs/ticket", { mapId: this.map.id });
    if (generation !== this.restartGeneration) return;
    this.ticket = result.ticket;
    this.hash = result.hash;
    this.state = spawn(this.map);
    this.previousPosition = [...this.state.p];
    this.controls.yaw = normalizeYaw(this.map.yaw);
    this.controls.pitch = 0;
  }
  async enter() {
    if (!this.state) return;
    this.controls.arm();
  }
  resume() {
    this.controls.resume();
  }
  leave() {
    this.restartGeneration++;
    this.loading++;
    this.loadId = "";
    document.querySelector("#capture-prompt")?.remove();
    this.controls.release(false);
    this.state = undefined;
    this.world = undefined;
    this.map = undefined;
    this.menu = true;
    this.network.send({ type: "leave" });
    this.onMenu();
  }
  pause() {
    this.controls.release();
  }
  async replay(id: string, ghost = false) {
    const data = await request<Replay>("/api/replays/" + id);
    if (this.map?.id !== data.mapId) await this.load(data.mapId);
    const s = spawn(this.map!),
      world = this.world!,
      states: State[] = [structuredClone(s)];
    let i = 0;
    for (const input of frames(data.inputs)) {
      step(world, s, input);
      states.push(structuredClone(s));
      if (++i % 2000 === 0)
        await new Promise((resolve) => setTimeout(resolve, 0));
    }
    if (ghost) {
      this.ghostFrames = states;
      toast("Ghost loaded. Restart to race.");
    } else {
      this.replayFrames = states;
      this.replayTick = 0;
      this.replayMode = true;
      this.spectating = "";
      this.menu = false;
      await this.controls.lock();
    }
  }
  spectate(id: string) {
    this.spectating = id;
    this.network.send({ type: "spectate", id });
    if (id) {
      this.menu = false;
      void this.controls.lock();
    }
  }
  cycleSpectator(direction: number) {
    const ids = (this.network.room?.players ?? [])
      .filter((p: any) => p.id !== this.user?.id)
      .map((p: any) => p.id);
    if (ids.length)
      this.spectate(
        ids[
          (ids.indexOf(this.spectating) + direction + ids.length) % ids.length
        ],
      );
  }
  toggleNoclip() {
    if (!this.user?.admin) return;
    this.noclip = !this.noclip;
    this.localPractice = true;
    if (this.state) this.state.assisted = true;
    toast(
      this.noclip
        ? "Admin noclip · unranked"
        : "Noclip off · restart for a ranked run",
    );
  }
  private async finish() {
    if (this.submitting || !this.state) return;
    this.submitting = true;
    const generation = this.restartGeneration;
    this.audio.play("finish");
    if (this.state.assisted || this.localPractice) {
      toast("Practice complete · restart for a verified run.");
      return;
    }
    try {
      const result = await request("/api/runs", {
        ticket: this.ticket,
        protocol: 1,
        movementVersion: PHYSICS_VERSION,
        tickrate: TICK_RATE,
        mapId: this.map!.id,
        hash: this.hash,
        inputs: this.inputs,
      });
      if (generation !== this.restartGeneration) return;
      toast(
        formatTime(result.seconds) +
          (result.record
            ? " · SERVER RECORD"
            : result.pb
              ? " · NEW PB"
              : " · VERIFIED") +
          " · +" +
          result.xp +
          " XP",
      );
      if (result.crateStatus?.reason) toast(result.crateStatus.reason);
      for (const award of result.awards) toast("Unlocked: " + award);
      this.onFinish(result);
    } catch (e) {
      if (generation === this.restartGeneration)
        toast("Run not saved: " + (e as Error).message);
    }
  }
  private frame(now: number) {
    requestAnimationFrame((t) => this.frame(t));
    const elapsed = now - this.last;
    this.performance.frameMs = elapsed;
    Object.assign(this.performance, this.network.metrics(now));
    if (elapsed > 50) this.performance.longFrames++;
    const simulationAt = performance.now();
    this.performance.ticks = 0;
    const dt = Math.min(elapsed / 1000, 0.1);
    this.last = now;
    if (
      this.state &&
      this.world &&
      !this.menu &&
      this.controls.locked &&
      !this.spectating
    ) {
      this.accumulator += dt;
      let iterations = 0;
      while (this.accumulator >= 0.01 && iterations++ < 10) {
        this.accumulator -= 0.01;
        if (this.replayMode) {
          this.replayTick = Math.min(
            this.replayTick + 1,
            this.replayFrames.length - 1,
          );
          continue;
        }
        this.previousPosition = [...this.state.p];
        const wasFinished = this.state.finishTick >= 0;
        this.performance.ticks++;
        this.tickSample++;
        const sample = this.controls.sample();
        if (this.noclip) {
          const c = Math.cos(sample.yaw),
            s = Math.sin(sample.yaw);
          this.state.p[0] += (c * sample.f - s * sample.s) * 8;
          this.state.p[1] += (s * sample.f + c * sample.s) * 8;
          this.state.p[2] += (Number(sample.jump) - Number(sample.duck)) * 8;
          this.state.yaw = sample.yaw;
          this.state.pitch = sample.pitch;
          continue;
        }
        const previous = {
          startTick: this.state.startTick,
          splits: this.state.splits.length,
          grounded: this.state.grounded,
          jumps: this.state.jumps,
        };
        step(
          this.world,
          this.state,
          wasFinished ? sample : record(this.inputs, sample),
        );
        if (previous.startTick < 0 && this.state.startTick >= 0)
          this.audio.play("start");
        if (this.state.splits.length > previous.splits)
          this.audio.play("checkpoint");
        if (this.state.jumps > previous.jumps) this.audio.play("jump");
        else if (this.state.grounded && !previous.grounded)
          this.audio.play("land");
        if (
          (this.state.trigger.includes("water") ||
            this.state.trigger.includes("ladder")) &&
          now - this.lastStepSound > 500
        ) {
          this.audio.play(
            this.state.trigger.includes("water") ? "water" : "ladder",
          );
          this.lastStepSound = now;
        }
        if (
          this.state.grounded &&
          speed(this.state.v) > 50 &&
          now - this.lastStepSound > 350
        ) {
          this.audio.play("step");
          this.lastStepSound = now;
        }
        if (!wasFinished && this.state.tick >= 120000) {
          this.localPractice = true;
          toast("Recording limit reached. Restart to submit a run.");
          this.pause();
        }
        if (!wasFinished && this.state.finishTick >= 0) void this.finish();
      }
    } else this.accumulator = 0;
    this.performance.simulationMs = performance.now() - simulationAt;
    if (now - this.tickSampleAt >= 1000) {
      this.performance.physicsHz = Math.round(
        (this.tickSample * 1000) / (now - this.tickSampleAt),
      );
      this.tickSample = 0;
      this.tickSampleAt = now;
    }
    const remote = this.network.players.get(this.spectating);
    const viewed: State | undefined = remote
      ? {
          ...this.state!,
          ...remote,
          tick: Math.round(remote.time * 100),
          startTick: 0,
          finishTick: -1,
        }
      : this.replayMode
        ? this.replayFrames[this.replayTick]
        : this.state;
    if (
      now - this.lastNet > 50 &&
      this.state &&
      !this.menu &&
      !this.replayMode &&
      !this.spectating
    ) {
      this.lastNet = now;
      const s = this.state;
      this.network.send({
        type: "state",
        state: {
          p: s.p,
          v: s.v,
          yaw: s.yaw,
          pitch: s.pitch,
          duck: s.duck,
          grounded: s.grounded,
          time:
            s.startTick < 0
              ? 0
              : ((s.finishTick < 0 ? s.tick : s.finishTick) - s.startTick) /
                100,
          stage: s.stage,
        },
      });
    }
    if (now - this.lastPing > 3000) {
      this.lastPing = now;
      this.network.send({ type: "ping", at: performance.now() });
    }
    const interval = settings.fpsLimit > 0 ? 1000 / settings.fpsLimit : 0;
    if (interval && now - this.lastRender + 0.1 < interval) return;
    const renderDt = Math.min((now - this.actualDraw) / 1000, 0.1);
    this.fps = Math.round(1000 / Math.max(1, now - this.actualDraw));
    this.actualDraw = now;
    this.performance.fps = this.fps;
    this.lastRender = interval
      ? now - ((now - this.lastRender) % interval)
      : now;
    const renderAt = performance.now();
    for (const [id, s] of this.network.players)
      this.renderer.setRemote(id, s, renderDt);
    const ghostStart = this.ghostFrames.at(-1)?.startTick ?? 0;
    const ghostIndex =
      this.state && this.state.startTick >= 0
        ? ghostStart + this.state.tick - this.state.startTick
        : 0;
    const ghost = this.ghostFrames[ghostIndex];
    this.renderer.setGhost(ghost ?? null);
    let presentation = viewed;
    if (viewed && !remote && !this.replayMode)
      presentation = presentPlayer(
        viewed,
        this.previousPosition,
        this.accumulator,
        this.controls,
      );
    this.renderer.draw(
      this.menu ? null : (presentation ?? null),
      this.menu,
      renderDt,
      viewed?.tick ?? 0,
      this.debug,
      !this.spectating || !this.thirdPerson,
    );
    this.performance.renderMs = performance.now() - renderAt;
    this.hud.hidden = this.menu;
    if (viewed && !this.menu && now - this.hudAt >= 100) {
      this.hudAt = now;
      const uiAt = performance.now();
      const time =
        viewed.startTick < 0
          ? 0
          : ((viewed.finishTick < 0 ? viewed.tick : viewed.finishTick) -
              viewed.startTick) /
            100;
      const label = this.spectating
        ? "SPECTATING " +
          (this.network.room?.players.find((p: any) => p.id === this.spectating)
            ?.name ?? "")
        : this.replayMode
          ? "REPLAY"
          : this.noclip
            ? "ADMIN NOCLIP · UNRANKED"
            : this.state?.assisted || this.localPractice
              ? "PRACTICE"
              : "";
      this.hud.innerHTML =
        '<div class="hud-top"><span>froghopping <b>/</b> ' +
        escape(this.map?.name) +
        "</span><span>" +
        escape(
          label || "STAGE " + (viewed.stage + 1) + " / " + this.map?.stages,
        ) +
        "</span><span>100 TICK <b>•</b> " +
        this.fps +
        ' FPS</span></div><div class="crosshair">+</div><div class="hud-bottom">' +
        (settings.timer
          ? '<div class="run-time">' + formatTime(time) + "</div>"
          : "") +
        (settings.velocity
          ? '<div class="velocity">' +
            Math.round(speed(viewed.v)) +
            " <small>u/s</small></div>"
          : "") +
        (settings.pbDelta && (remote?.pb ?? this.pb) != null
          ? '<div class="pb-comparison">PB ' +
            formatTime(remote?.pb ?? this.pb) +
            (!remote && this.pb != null && time > this.pb
              ? " · +" + (time - this.pb).toFixed(3)
              : "") +
            "</div>"
          : "") +
        '<div class="keys">' +
        ["W", "A", "S", "D"]
          .map(
            (k) =>
              '<i class="' +
              (this.controls.keys.has("Key" + k) ? "pressed" : "") +
              '">' +
              k +
              "</i>",
          )
          .join("") +
        '</div><span class="hud-hint">' +
        (viewed.finishTick >= 0
          ? "FINISHED · R TO RESTART"
          : "SPACE HOP · R RESTART · T STAGE · ESC MENU") +
        "</span></div>" +
        (settings.binds.scoreboard?.some((key) => this.controls.keys.has(key))
          ? `<div class="scoreboard"><h2>${escape(this.map?.name)}</h2>${(
              this.network.room?.players ?? [this.user]
            )
              .filter(Boolean)
              .map(
                (p: any) =>
                  `<p><span>${escape(p.name)}</span><span>${p.id === this.user?.id ? formatTime(time) : formatTime(this.network.players.get(p.id)?.time)}</span></p>`,
              )
              .join("")}</div>`
          : "") +
        (this.debug
          ? '<pre class="debug">' +
            escape(
              JSON.stringify(
                {
                  position: viewed.p,
                  velocity: viewed.v,
                  grounded: viewed.grounded,
                  trigger: viewed.trigger,
                  stage: viewed.stage,
                  tick: viewed.tick,
                  ping: this.network.ping,
                  ...this.performance,
                  ...this.renderer.metrics(),
                },
                null,
                2,
              ),
            ) +
            "</pre>"
          : "");
      this.performance.uiMs = performance.now() - uiAt;
    }
  }
}
